import React, { useCallback, useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { TicketPercent, Plus, Trash2, Save, X } from "lucide-react";

export default function AdminDiscountCodes() {
  const [codes, setCodes] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/discount-codes"); setCodes(data); }
    catch (e) { toast.error(formatError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (c) => {
    try { await api.patch(`/admin/discount-codes/${c.id}`, { active: !c.active }); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.code}?`)) return;
    try { await api.delete(`/admin/discount-codes/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const setCap = async (c) => {
    const v = window.prompt(`Max redemptions for ${c.code}? (blank = unlimited)`, c.max_redemptions || "");
    if (v === null) return;
    const max = v.trim() === "" ? null : Number(v);
    try { await api.patch(`/admin/discount-codes/${c.id}`, { max_redemptions: max }); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6" data-testid="admin-discount-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold sm:text-4xl">Discount Codes</h1>
          <p className="mt-1 text-sm font-medium text-sarthi-muted">Create, cap, disable or delete promo codes. Applied to Premium Plans only.</p>
        </div>
        <button data-testid="new-discount-btn" onClick={() => setOpen(true)} className="sarthi-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm text-white">
          <Plus className="h-4 w-4" /> New Code
        </button>
      </div>

      <div className="sarthi-card overflow-x-auto">
        <table className="w-full text-left">
          <thead className="border-b-2 border-sarthi-border bg-sarthi-accent/50 text-xs font-bold uppercase text-sarthi-muted">
            <tr><th className="px-3 py-3">Code</th><th className="px-3 py-3">Off</th><th className="px-3 py-3">Message</th><th className="px-3 py-3">Redemptions</th><th className="px-3 py-3">Cap</th><th className="px-3 py-3">Valid Until</th><th className="px-3 py-3">Active</th><th className="px-3 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {codes.map((c) => (
              <tr key={c.id} data-testid={`code-row-${c.id}`} className="border-b border-sarthi-border/60">
                <td className="px-3 py-3 font-mono font-bold text-sarthi-primary">{c.code}</td>
                <td className="px-3 py-3 font-bold">{c.percent_off}%</td>
                <td className="px-3 py-3 text-xs text-sarthi-muted">{c.message}</td>
                <td className="px-3 py-3 text-sm">{c.redemptions || 0}</td>
                <td className="px-3 py-3 text-sm">
                  <button data-testid={`cap-${c.id}`} onClick={() => setCap(c)} className="text-xs font-bold text-sarthi-primary underline">
                    {c.max_redemptions ?? "∞"}
                  </button>
                </td>
                <td className="px-3 py-3 text-xs text-sarthi-muted">{(c.valid_until || "").slice(0, 10) || "—"}</td>
                <td className="px-3 py-3">
                  <button data-testid={`toggle-${c.id}`} onClick={() => toggleActive(c)} className={`rounded-full px-2 py-0.5 text-xs font-bold ${c.active ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                    {c.active ? "ACTIVE" : "DISABLED"}
                  </button>
                </td>
                <td className="px-3 py-3 text-right">
                  <button data-testid={`delete-${c.id}`} onClick={() => del(c)} className="text-red-700 hover:text-red-900"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {codes.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-sarthi-muted"><TicketPercent className="mx-auto mb-2 h-8 w-8 opacity-50" />No promo codes yet</td></tr>}
          </tbody>
        </table>
      </div>

      {open && <CreateModal onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function CreateModal({ onClose, onSaved }) {
  const [f, setF] = useState({ code: "", percent_off: 10, message: "", valid_until: "", max_redemptions: "" });
  const submit = async () => {
    try {
      const body = { ...f, percent_off: Number(f.percent_off) };
      if (f.max_redemptions === "" || f.max_redemptions === null) body.max_redemptions = null;
      else body.max_redemptions = Number(f.max_redemptions);
      if (f.valid_until) body.valid_until = new Date(f.valid_until).toISOString();
      else delete body.valid_until;
      await api.post("/admin/discount-codes", body);
      toast.success("Discount code created");
      onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="create-code-modal">
      <div className="sarthi-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between"><h2 className="font-display text-xl font-extrabold">New Discount Code</h2><button onClick={onClose}><X className="h-5 w-5" /></button></div>
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="new-code" placeholder="Code (e.g. LAUNCH50)" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} className="col-span-2 rounded-xl border-2 border-sarthi-border px-3 py-2 font-mono font-bold uppercase" />
          <input data-testid="new-percent" type="number" min="1" max="99" placeholder="% off" value={f.percent_off} onChange={set("percent_off")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="new-cap" type="number" placeholder="Max uses (blank=∞)" value={f.max_redemptions} onChange={set("max_redemptions")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="new-message" placeholder="Message shown at checkout" value={f.message} onChange={set("message")} className="col-span-2 rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="new-valid-until" type="date" value={f.valid_until} onChange={set("valid_until")} className="col-span-2 rounded-xl border-2 border-sarthi-border px-3 py-2" />
        </div>
        <button data-testid="save-code" onClick={submit} className="sarthi-btn-primary mt-4 inline-flex w-full items-center justify-center gap-2 py-2 text-white"><Save className="h-4 w-4" /> Create</button>
      </div>
    </div>
  );
}
